import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import Button from "components/buttons/Button";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { Modal } from "components/Modal/Modal";
import { ModalRiskKycPresentTabs } from "configs/types";
import RiskKycPresetMainTab from "../tabs/RiskKycPresetMainTab";
import RiskKycPresetRulesTab from "../tabs/RiskKycPresetRulesTab";
import {
  getRiskKycPresetsItem,
  handleClearDraftItem,
  RiskKycPresetItem,
  handleUpdateDraftItem,
  updateRiskKycPresetsItem,
  createRiskKycPresetsItem,
  addRiskKycPresetsRules,
  deleteRiskKycPresetsRules
} from "store/risk/kyc/presets/slice";
import ModalSkeleton from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { useFormErrors } from "helpers/useFormErrors";
import { usePermissionHelper } from "helpers/usePermissionHelper";

const RiskKycPresetsEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const formErrors = useFormErrors();
  const permissions = usePermissionHelper();

  const isItemFetched = useAppSelector(
    (store) => store.riskKycPresets.isItemFetched,
  );

  const isItemFetching = useAppSelector(
    (store) => store.riskKycPresets.isItemFetching,
  );
  const draftItem = useAppSelector((store) => store.riskKycPresets.draftItem);

  const tabs = [ModalRiskKycPresentTabs.main, ModalRiskKycPresentTabs.rules];

  const entityId = searchParams.get("entityId") ?? "";

  const riskKycRules = useAppSelector((store) => store.riskKycRules.list).filter(v => v.isActive);
  const assignedRules = draftItem.assignedRules || [];

  const unassignedRules = riskKycRules.filter(r => !assignedRules.find(rule => rule.ruleDefinitionId === r.id));

  const [activeTab, setActiveTab]
    = useState<ModalRiskKycPresentTabs>(ModalRiskKycPresentTabs.main);
  
  const [selectedToUnassignRules, setSelectedToUnassignRules] = useState<number[]>([]);
  const [selectedToAssignRules, setSelectedToAssignRules] = useState<number[]>([]);

  const handleClose = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearDraftItem());
  };

  const handleSave = async () => {
    if (!draftItem.title || !draftItem.code) {

      if (!draftItem.title) formErrors.setFieldError('title', t('NotEmpty'));
      if (!draftItem.code) formErrors.setFieldError('code', t('NotEmpty'));
      
      return;
    }

    if (entityId) {
      dispatch(updateRiskKycPresetsItem(draftItem as RiskKycPresetItem))
        .unwrap()
        .then(() => {
          dispatch(
            showNotification({
              title: t("success"),
              message: t("entityUpdateSuccess"),
              type: NotificationTypes.success,
            }),
          );
        })
        .catch((e) => {
          dispatch(
            showNotification({
              title: t("error"),
              message: t("entityUpdateError"),
              type: NotificationTypes.error,
            }),
          );
        });
      
      return;
    }

    dispatch(createRiskKycPresetsItem(draftItem as RiskKycPresetItem))
      .unwrap()
      .then(() => {
        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );

        handleClose();
      })
      .catch(() => {
        dispatch(
          showNotification({
            title: t("error"),
            message: t("entityCreateError"),
            type: NotificationTypes.error,
          }),
        );
      });
  };

  const handleUpdateField = <
    K extends keyof RiskKycPresetItem,
  >(
    key: K,
    value: RiskKycPresetItem[K],
  ) => {
    const updatedField = { [key]: value };
    dispatch(handleUpdateDraftItem(updatedField));
  };

  const handleTabClick = (tab: ModalRiskKycPresentTabs) => {

    if (!entityId && tab === ModalRiskKycPresentTabs.rules) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createPresetFirst"),
          type: NotificationTypes.warning,
        }),
      );
      
      return;
    }

    setActiveTab(tab);
  };

  const onSelectAssignedRules = (id: number) => {
    setSelectedToUnassignRules(c => {
      if (c.includes(id)) return c.filter(v => v !== id);

      return [...c, id];
    });
  };

  const onSelectUnassignedRules = (id: number) => {
    setSelectedToAssignRules(c => {
      if (c.includes(id)) return c.filter(v => v !== id);

      return [...c, id];
    });
  };

  const onAssignRules = () => {
    const rulesToAssign = selectedToAssignRules.map((r, index) => ({
      ruleDefinitionId: r,
      isActive: true,
      position: index === 0 ? 10 : index * 10
    }));

    dispatch(addRiskKycPresetsRules({
      presetId: +entityId,
      rules: rulesToAssign
    }))
      .unwrap()
      .then(() => {
        setSelectedToAssignRules([]);
        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      })
      .catch(() => {
        dispatch(
          showNotification({
            title: t("error"),
            message: t("entityUpdateError"),
            type: NotificationTypes.error,
          }),
        );
      })
  };

  const onUnassignRules = () => {
    dispatch(deleteRiskKycPresetsRules(selectedToUnassignRules))
      .unwrap()
      .then(() => {
        setSelectedToUnassignRules([]);
        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityDeleteSuccess"),
            type: NotificationTypes.success,
          }),
        );
      })
      .catch(() => {
        dispatch(
          showNotification({
            title: t("error"),
            message: t("entityDeleteError"),
            type: NotificationTypes.error,
          }),
        );
      })
  };

  const renderTab = () => {

    if (activeTab === ModalRiskKycPresentTabs.main) {
      return (
        <RiskKycPresetMainTab
          payload={draftItem}
          handleUpdateField={handleUpdateField}
          formError={formErrors}
          readOnly={!permissions?.update}
        />
      )
    }

    if (activeTab === ModalRiskKycPresentTabs.rules) {
      return (
        <RiskKycPresetRulesTab
          assignedRules={assignedRules}
          unassignedRules={unassignedRules}
          selectedToUnassignRules={selectedToUnassignRules}
          selectedToAssignRules={selectedToAssignRules}
          onSelectAssignedRules={onSelectAssignedRules}
          onSelectUnassignedRules={onSelectUnassignedRules}
          onAssignRules={onAssignRules}
          onUnassignRules={onUnassignRules}
          readOnly={!permissions?.update}
        />
      )
    }

    return null;
  };


  const isLoaded = () => {
    if (entityId !== "" && !isItemFetched) {
      return false;
    }

    return true;
  };

  const canBeApplied = () => {
    if (formErrors.hasError('title') || formErrors.hasError('code')) {
      return true;
    }

    return false;
  };


  useEffect(() => {
    if (entityId !== "" && !isItemFetched) {
      dispatch(getRiskKycPresetsItem(+entityId));
    }
  }, [searchParams, isItemFetched]);

  return (
    <>
      <Modal onClose={handleClose} size="sm">
        <Modal.Header>{t(`${entityId ? 'kycPresetEdit' : 'kycPresetCreate'}`)}</Modal.Header>
        {!isLoaded() ? <ModalSkeleton /> : (
          <>
            <Modal.Tabs
              tabs={tabs}
              activeTab={activeTab}
              onClick={handleTabClick}
            />
            <Modal.Content>
              <div className="promo-content" style={activeTab === ModalRiskKycPresentTabs.main ? { height: "100%", maxHeight: "none" } : undefined}>{renderTab()}</div>
            </Modal.Content>
            {permissions?.update &&
              activeTab === ModalRiskKycPresentTabs.main && (
              <Modal.Footer>
                <Button
                  isSecondary txt={t("cancel")}
                  handleClick={handleClose}
                />
                <Button
                  isPrimary
                  handleClick={handleSave}
                  txt={t("save")}
                  isLoad={isItemFetching}
                  disabled={canBeApplied()}
                />
              </Modal.Footer>
              )}
          </>
        )}
      </Modal>
    </>
  );
};

export default RiskKycPresetsEditModal;
